import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, XCircle, Clock, AlertTriangle } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface TrialEligibilityBannerProps {
  eligibility: {
    eligible: boolean;
    reason?: string;
    blockedUntil?: Date;
    hasActiveTrial?: boolean;
    daysRemaining?: number;
    trialEndDate?: Date;
    hasActiveSubscription?: boolean;
    firmName?: string;
  };
  fcaNumber: string;
}

export function TrialEligibilityBanner({ eligibility, fcaNumber }: TrialEligibilityBannerProps) {
  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Active trial in progress
  if (eligibility.hasActiveTrial && eligibility.daysRemaining !== undefined) {
    const totalDays = 30;
    const progressPercentage = ((totalDays - eligibility.daysRemaining) / totalDays) * 100;

    return (
      <Alert className="border-blue-500 bg-blue-50">
        <Clock className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Active Trial In Progress</strong>
          {eligibility.firmName && (
            <div className="mt-1 text-sm">
              Firm: {eligibility.firmName} (FCA: {fcaNumber})
            </div>
          )}
          <div className="mt-2 space-y-2">
            <div className="flex justify-between text-sm">
              <span>{eligibility.daysRemaining} days remaining</span>
              {eligibility.trialEndDate && (
                <span>Ends: {formatDate(eligibility.trialEndDate)}</span>
              )}
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>
          <div className="mt-2 text-sm">
            An account already exists for this FCA number. Please contact your company administrator to be added to the account.
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Active subscription exists
  if (eligibility.hasActiveSubscription) {
    return (
      <Alert className="border-blue-500 bg-blue-50">
        <AlertTriangle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Account Already Exists</strong>
          {eligibility.firmName && (
            <div className="mt-1 text-sm">
              Firm: {eligibility.firmName} (FCA: {fcaNumber})
            </div>
          )}
          <div className="mt-2 text-sm">
            An active account exists for this FCA number. Please contact your company administrator to be added to the account.
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Eligible for new trial
  if (eligibility.eligible) {
    return (
      <Alert className="border-green-500 bg-green-50">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          <strong>30-Day Free Trial Available</strong>
          {eligibility.firmName && (
            <div className="mt-1 text-sm">
              Firm: {eligibility.firmName} (FCA: {fcaNumber})
            </div>
          )}
          <div className="mt-2 text-sm">
            Your firm is eligible for a free 30-day trial. You'll need to provide payment details, but won't be charged until the trial ends.
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Not eligible (cooldown period)
  return (
    <Alert className="border-red-500 bg-red-50">
      <XCircle className="h-4 w-4 text-red-600" />
      <AlertDescription className="text-red-800">
        <strong>Trial Not Available</strong>
        {eligibility.firmName && (
          <div className="mt-1 text-sm">
            Firm: {eligibility.firmName} (FCA: {fcaNumber})
          </div>
        )}
        <div className="mt-2 text-sm">
          {eligibility.reason}
        </div>
        {eligibility.blockedUntil && (
          <div className="flex items-center gap-2 mt-2 text-sm">
            <Clock className="h-3 w-3" />
            Trial available from: {formatDate(eligibility.blockedUntil)}
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-red-200 text-sm">
          <p className="font-semibold mb-1">Why can't I start a new trial?</p>
          <p>
            A trial for this FCA number was recently used. This prevents misuse while ensuring legitimate firms can access our platform. 
            If you believe this is an error, please contact support.
          </p>
        </div>
      </AlertDescription>
    </Alert>
  );
}
